<script>
import useValidation from "@/use/Validation";
import {computed, onMounted, ref} from "vue";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import {useAutoPaySectionStore} from "@/stores/AutoPaySection";
import StrVariableAutocomplete from "@/views/AutoPaySection/Components/StrVariableAutocomplete.vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    components: {StrVariableAutocomplete},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        
        const readonly = computed(() => props.deleteAction || props.viewAction);
        
        const {loading, overlay, dialog} = useSharedData();
        
        const {operations} = useSharedSelectData();
        
        const {$route, $refs} = useTools();
        
        const {getBack, isTwoTest} = useSharedMethod();
        
        const autoPaySectionStore = useAutoPaySectionStore();
        
        const autoPaySection = ref({conditions: []});
        
        
        function findById(id) {
            loading.value = true;
            axios.get(`/autoPaySections/${id}`).then((response) => {
                autoPaySection.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/autoPaySections', autoPaySection.value).then((response) => {
                    autoPaySectionStore.addAutoPaySection(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/autoPaySections', autoPaySection.value).then((response) => {
                    autoPaySectionStore.updateAutoPaySection(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/autoPaySections/${autoPaySection.value.id}`).then(() => {
                autoPaySectionStore.deleteAutoPaySection(autoPaySection.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function addCondition() {
            autoPaySection.value.conditions.push({
                order: autoPaySection.value.conditions.length + 1,
            });
        }
        
        function removeCondition(index) {
            autoPaySection.value.conditions.splice(index, 1);
        }
        
        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });
        
        return {
            mxRules,
            readonly,
            autoPaySectionStore,
            loading,
            overlay,
            dialog,
            autoPaySection,
            operations,
            save,
            update,
            remove,
            isTwoTest,
            addCondition,
            removeCondition,
            getBack,
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="900"
        scrollable
    >
        <v-card flat>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @cancel="overlay = false"
                @remove="remove"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.details')">
                            <v-container fluid>
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="autoPaySection.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.requied]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('auto_pay_sections.default_value')" required/>
                                        <w-text-field
                                            v-model="autoPaySection.initialValue"
                                            type="number"
                                            :readonly="readonly"
                                            :rules="[mxRules.requied]"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('auto_pay_sections.variable')" required/>
                                        <str-variable-autocomplete
                                            v-model="autoPaySection.variable"
                                            :readonly="readonly"
                                            :rules="[mxRules.requied]"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </w-fieldset>
                        <br/>
                        <w-fieldset
                            :title="$t('common.details')"
                            show-add-action
                            @add="addCondition"
                        >
                            <v-container fluid>
                                <v-row
                                    dense
                                    v-for="(condition, index) in autoPaySection.conditions"
                                    :key="index"
                                >
                                    <v-col cols="3">
                                        <w-label :label="$t('common.operation')" required/>
                                        <w-enum-select
                                            v-model="condition.operator"
                                            :items="operations"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="3">
                                        <w-label :label="$t('common.first_test')" required/>
                                        <w-text-field
                                            v-model="condition.firstTest"
                                            type="number"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="3">
                                        <w-label :label="$t('common.second_test')" :required="isTwoTest(condition.operator)"/>
                                        <w-text-field
                                            v-model="condition.secondTest"
                                            :disabled="!isTwoTest(condition.operator)"
                                            type="number"
                                            :readonly="readonly"
                                            :rules="isTwoTest(condition.operator) ? [mxRules.required] : []"
                                        />
                                    </v-col>
                                    <v-col cols="2">
                                        <w-label :label="$t('common.result')" required/>
                                        <w-text-field
                                            v-model="condition.consequence"
                                            type="number"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="1">
                                        <w-btn-icon
                                            :disabled="readonly"
                                            icon="mdi-trash-can"
                                            color="red"
                                            classe="mt-6"
                                            @press="removeCondition(index)"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>